import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, fmtDate, downloadFile } from "../lib/api";
import { QUAL_STATUSES, RESEARCH_STATUS } from "../lib/constants";
import { ResearchBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";
import { Checkbox } from "../components/ui/checkbox";
import ResearchApproval from "../components/ResearchApproval";
import { ClassBadge } from "../components/report/OwnerIntel";

const OWNER_FILTERS = [["highest", "Highest Qualified Prospects"], ["not_researched", "Owner Not Researched"], ["researched", "Owner Researched"],
  ["identified", "Owner Identified"], ["verified_email", "Verified Email Available"], ["failed", "Research Failed"]];

const postBlob = (path, ids, name) => api.post(path, { location_ids: ids }, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  a.click();
});

const BatchPanel = ({ id }) => {
  const [b, setB] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/owner-research/batches/${id}`).then((r) => setB(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);
  if (!b) return null;
  const s = b.stats;
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm" data-testid="batch-progress">
      <div className="mb-2 text-sm font-bold">Owner research batch · {new Date(b.created_at).toLocaleString()}</div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs sm:grid-cols-5 lg:grid-cols-9">
        {[["Total", s.total], ["Completed", s.completed], ["In progress", s.in_progress], ["Owner identified", s.owner_identified], ["Owner not identified", s.owner_not_identified],
          ["Email found", s.email_found], ["Email verified", s.email_verified], ["Failed", s.failed], ["Est. cost", `$${s.known_cost}${s.has_unpriced_usage ? "+?" : ""}`]].map(([l, v]) => (
          <div key={l} className="rounded bg-slate-50 p-2"><div className="font-mono text-lg font-bold">{v}</div><div className="text-slate-500">{l}</div></div>))}
      </div>
    </div>
  );
};

const ALL = "__all";
const Filter = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger className="w-full sm:w-44" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Leads() {
  const { user } = useAuth();
  const [f, setF] = useState({ q: "", city: "", research_status: "", qualification: "", assigned_to: "", opportunity: "", owner: "" });
  const [sel, setSel] = useState([]);
  const [approve, setApprove] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/locations", { params: f }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);
  const names = Object.fromEntries(team.map((u) => [u.id, u.name]));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Pipeline</div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight">Saved leads</h1>
        </div>
        <Button variant="outline" onClick={() => downloadFile("/exports/leads.csv", "360rge-leads.csv").catch((e) => toast.error(errMsg(e)))} data-testid="export-leads-csv-btn"><FileDown className="mr-1 h-4 w-4" />Export CSV</Button>
      </div>
      <div className="flex flex-col gap-2 rounded-lg border bg-white p-4 sm:flex-row sm:flex-wrap">
        <div className="relative sm:w-56"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Restaurant name" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="leads-search-input" /></div>
        <Input className="sm:w-44" placeholder="City / ZIP" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="leads-city-input" />
        <Filter value={f.research_status} onChange={(v) => set("research_status", v)} placeholder="Any research status" options={Object.entries(RESEARCH_STATUS).map(([k, v]) => [k, v.label])} testId="leads-research-filter" />
        <Filter value={f.qualification} onChange={(v) => set("qualification", v)} placeholder="Any qualification" options={QUAL_STATUSES.map((s) => [s, s])} testId="leads-qualification-filter" />
        <Filter value={f.opportunity} onChange={(v) => set("opportunity", v)} placeholder="Any opportunity" options={services.map((s) => [s.key, s.name])} testId="leads-opportunity-filter" />
        {user?.role === "admin" && <Filter value={f.assigned_to} onChange={(v) => set("assigned_to", v)} placeholder="Any salesperson" options={team.map((u) => [u.id, u.name])} testId="leads-salesperson-filter" />}
        <Filter value={f.owner} onChange={(v) => set("owner", v)} placeholder="All prospects" options={OWNER_FILTERS} testId="leads-owner-filter" />
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="bulk-bar">
        <span className="text-sm text-slate-600">{sel.length} selected · ranked by opportunity score</span>
        <Button size="sm" className="bg-orange-600 hover:bg-orange-700" disabled={!sel.length} onClick={() => setApprove(true)} data-testid="research-selected-owners-btn">Research Selected Owners</Button>
        <Button size="sm" variant="outline" disabled={!sel.length} onClick={() => postBlob("/exports/selected.csv", sel, "360rge-owner-contacts.csv").catch((e) => toast.error(errMsg(e)))} data-testid="export-selected-csv-btn">Selected CSV</Button>
        <Button size="sm" variant="outline" disabled={!sel.length} onClick={() => postBlob("/exports/selected.xlsx", sel, "360rge-leads.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="export-selected-xlsx-btn">Selected XLSX</Button>
      </div>
      {approve && <ResearchApproval title={`Approve owner research for ${sel.length} selected restaurant(s)`} submit={(b) => api.post("/owner-research/batch", { location_ids: sel, paid: b.paid, confirm: b.approved, approve_unpriced: b.approve_unpriced }).then((r) => r.data)}
        onClose={() => setApprove(false)} onStarted={(d) => { setApprove(false); setBatchId(d.id); setSel([]); }} />}
      {batchId && <BatchPanel id={batchId} />}
      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="w-full min-w-[760px] text-left text-sm" data-testid="leads-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-3"><Checkbox checked={rows.length > 0 && sel.length === rows.length} onCheckedChange={(v) => setSel(v ? rows.map((r) => r.id) : [])} data-testid="select-all-leads" /></th><th className="px-4 py-3">Restaurant</th><th className="px-4">Priority</th><th className="px-4">Research</th><th className="px-4">Opportunities</th><th className="px-4">Qualification</th><th className="px-4">Salesperson</th><th className="px-4">Follow-up</th></tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-t hover:bg-orange-50/40" data-testid={`lead-row-${l.id}`}>
                <td className="px-3"><Checkbox checked={sel.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`lead-select-${l.id}`} /></td>
                <td className="px-4 py-3"><Link to={`/leads/${l.id}`} className="font-semibold hover:text-orange-700 hover:underline">{l.name}</Link>{l.do_not_contact && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-800" data-testid={`lead-dnc-badge-${l.id}`}>Do not contact</span>}<div className="text-xs text-slate-500">{l.address}</div>{l.contact_research_status && <div className="text-[11px] text-slate-500">Contacts: {l.contact_research_status}</div>}</td>
                <td className="px-4"><ClassBadge ls={l.lead_scores} testId={`lead-class-${l.id}`} />{l.lead_scores && <div className="font-mono text-[11px] text-slate-500">opp {l.lead_scores.scores.opportunity ?? "—"} · owner {l.lead_scores.owner_level}</div>}</td>
                <td className="px-4"><ResearchBadge status={l.research_status} /><div className="text-[11px] text-slate-500">{fmtDate(l.last_researched_at)}</div></td>
                <td className="max-w-[240px] px-4 text-xs">{(l.opportunity_titles || []).filter(Boolean).join(" · ") || <span className="text-slate-400">{l.last_researched_at ? "None supported" : "Not researched"}</span>}</td>
                <td className="px-4 text-xs font-medium">{l.qualification_status}</td>
                <td className="px-4 text-xs">{names[l.assigned_to] || "—"}</td>
                <td className="px-4 text-xs">{l.follow_up_date || "—"}{l.next_action && <div className="text-slate-500">{l.next_action}</div>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500" data-testid="leads-empty">No saved leads match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500">CSV export includes your lead data and our own findings. Google ratings and review content are excluded per Google Maps Platform terms.</p>
    </div>
  );
}
